import { attributeDefinitionIdFormat } from "@redotech/redo-ids/ids";
import { GshopVariantFieldType } from "@redotech/redo-prisma-client/enums";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { AttributeType, AttributeValueSchema } from "../attributes";
import { BaseProductListingSchema, ListingType } from "../base-listing";
import {
  GoogleShoppingAgeGroupSchema,
  GoogleShoppingAvailabilitySchema,
  GoogleShoppingConditionSchema,
  GoogleShoppingGenderSchema,
  GoogleShoppingPriceSchema,
  GoogleShoppingShippingDimensionSchema,
  GoogleShoppingShippingWeightSchema,
} from "./common-schemas";
import {
  GoogleShoppingListingMediaSchema,
  GoogleShoppingListingMediaWithGoogleIds,
  GoogleShoppingListingMediaWithoutRedoIds,
} from "./google-shopping-media";

export const GoogleShoppingVariantFieldTypeSchema = createEnum(
  GshopVariantFieldType,
);
export type GoogleShoppingVariantFieldType = z.infer<
  typeof GoogleShoppingVariantFieldTypeSchema
>;

export const GoogleShoppingVariantListingSchema_ =
  BaseProductListingSchema.extend({
    listingType: z.literal(ListingType.GoogleShopping),
    id: z.bigint(),
    offerId: z.string(),
    price: GoogleShoppingPriceSchema,
    salePrice: GoogleShoppingPriceSchema.nullable(),
    availability: GoogleShoppingAvailabilitySchema,
    condition: GoogleShoppingConditionSchema.nullable(),
    gtin: z.string().nullable(),
    mpn: z.string().nullable(),
    shippingWeight: GoogleShoppingShippingWeightSchema.nullable(),
    shippingLength: GoogleShoppingShippingDimensionSchema.nullable(),
    shippingWidth: GoogleShoppingShippingDimensionSchema.nullable(),
    shippingHeight: GoogleShoppingShippingDimensionSchema.nullable(),
    color: z.string().nullable(),
    size: z.string().nullable(),
    material: z.string().nullable(),
    pattern: z.string().nullable(),
    ageGroup: GoogleShoppingAgeGroupSchema.nullable(),
    gender: GoogleShoppingGenderSchema.nullable(),
    media: z.array(GoogleShoppingListingMediaSchema),
    createdAt: z.date(),
    updatedAt: z.date(),
  });
export const GoogleShoppingVariantListingSchema: z.ZodType<GoogleShoppingVariantListing> =
  GoogleShoppingVariantListingSchema_;
export interface GoogleShoppingVariantListing
  extends z.infer<typeof GoogleShoppingVariantListingSchema_> {}

export type GoogleShoppingVariantListingWithoutRedoIds = Omit<
  GoogleShoppingVariantListing,
  "id" | "productId" | "media"
> & { media: GoogleShoppingListingMediaWithoutRedoIds[] };

export type GoogleShoppingVariantListingWithGoogleIds = Omit<
  GoogleShoppingVariantListing,
  "media"
> & { media: GoogleShoppingListingMediaWithGoogleIds[] };

const GoogleShoppingVariantMappingSchema_ = z.object({
  fieldName: GoogleShoppingVariantFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const GoogleShoppingVariantMappingSchema: z.ZodType<GoogleShoppingVariantMapping> =
  GoogleShoppingVariantMappingSchema_;
export interface GoogleShoppingVariantMapping
  extends z.infer<typeof GoogleShoppingVariantMappingSchema_> {}

export const GoogleShoppingVariantAttributeType = {
  [GshopVariantFieldType.offer_id]: AttributeType.ShortText,
  [GshopVariantFieldType.price]: AttributeType.Money,
  [GshopVariantFieldType.sale_price]: AttributeType.Money,
  [GshopVariantFieldType.availability]: AttributeType.Enum,
  [GshopVariantFieldType.condition]: AttributeType.Enum,
  [GshopVariantFieldType.gtin]: AttributeType.ShortText,
  [GshopVariantFieldType.mpn]: AttributeType.ShortText,
  [GshopVariantFieldType.shipping_weight]: AttributeType.Weight,
  [GshopVariantFieldType.shipping_length]: AttributeType.Float,
  [GshopVariantFieldType.shipping_width]: AttributeType.Float,
  [GshopVariantFieldType.shipping_height]: AttributeType.Float,
  [GshopVariantFieldType.color]: AttributeType.ShortText,
  [GshopVariantFieldType.size]: AttributeType.ShortText,
  [GshopVariantFieldType.material]: AttributeType.ShortText,
  [GshopVariantFieldType.pattern]: AttributeType.ShortText,
  [GshopVariantFieldType.age_group]: AttributeType.Enum,
  [GshopVariantFieldType.gender]: AttributeType.Enum,
} satisfies Record<GshopVariantFieldType, AttributeType>;

export const GoogleShoppingVariantAttributeSchema = {
  [GshopVariantFieldType.offer_id]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.offer_id]
    ].nullable(),
  [GshopVariantFieldType.price]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.price]
    ].nullable(),
  [GshopVariantFieldType.sale_price]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.sale_price]
    ].nullable(),
  [GshopVariantFieldType.availability]:
    GoogleShoppingAvailabilitySchema.nullable(),
  [GshopVariantFieldType.condition]: GoogleShoppingConditionSchema.nullable(),
  [GshopVariantFieldType.gtin]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.gtin]
    ].nullable(),
  [GshopVariantFieldType.mpn]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.mpn]
    ].nullable(),
  [GshopVariantFieldType.shipping_weight]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.shipping_weight]
    ].nullable(),
  [GshopVariantFieldType.shipping_length]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.shipping_length]
    ].nullable(),
  [GshopVariantFieldType.shipping_width]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.shipping_width]
    ].nullable(),
  [GshopVariantFieldType.shipping_height]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.shipping_height]
    ].nullable(),
  [GshopVariantFieldType.color]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.color]
    ].nullable(),
  [GshopVariantFieldType.size]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.size]
    ].nullable(),
  [GshopVariantFieldType.material]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.material]
    ].nullable(),
  [GshopVariantFieldType.pattern]:
    AttributeValueSchema[
      GoogleShoppingVariantAttributeType[GshopVariantFieldType.pattern]
    ].nullable(),
  [GshopVariantFieldType.age_group]: GoogleShoppingAgeGroupSchema.nullable(),
  [GshopVariantFieldType.gender]: GoogleShoppingGenderSchema.nullable(),
};

export type GoogleShoppingVariantAttributeValueType = {
  [key in GshopVariantFieldType]: (typeof GoogleShoppingVariantAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getGoogleShoppingVariantAttributeValue<
  T extends GshopVariantFieldType,
>(field: T, value: unknown): GoogleShoppingVariantAttributeValueType[T] | null {
  return GoogleShoppingVariantAttributeSchema[field].parse(
    value,
  ) as GoogleShoppingVariantAttributeValueType[T];
}

export function getGoogleShoppingVariantAttributeType<
  T extends GshopVariantFieldType,
>(field: T): (typeof GoogleShoppingVariantAttributeType)[T] {
  return GoogleShoppingVariantAttributeType[field];
}
